import gql from "graphql-tag.macro";

const loadAllUsersQuery = gql`
    query loadAllUsersInProject {
        currentProject {
            ... on Project {
                _id
                availableusers {
                    edges {
                        node {
                            _id
                            firstname
                            lastname
                            email
                            avatar
                            groups {
                                ... on ProjectGroup {
                                    name
                                    id
                                    displayName
                                    hasChildGroups
                                    path
                                    created
                                    subGroups {
                                        id
                                    }
                                }
                            }
                        }
                    }
                }
                groups {
                    id
                    name
                    description
                    lastUpdated
                    members {
                        _id
                        firstname
                        lastname
                    }
                    hasRoles
                    displayName
                    hasChildGroups
                    path
                    created
                }
            }
        }
    }
`;

const loadActiveWorkflowModelQuery = gql`
    query loadActiveWorkflowModel {
        currentProject {
            ... on Project {
                _id
                activeHeadingWorkflowModel {
                    defaultDisabled
                    stages {
                        _id
                        name
                        description
                        nextStageTransitions {
                            to
                            name
                            description
                        }
                        prevStageTransitions {
                            to
                            name
                            description
                        }
                        PlannedDuration
                        onEnter {
                            explicit_deny {
                                subject
                                kind
                                permission
                                scope
                            }
                            createActions {
                                kind
                                name
                                customstati
                                subject {
                                    id
                                    kind
                                }
                            }
                        }
                        assigneeId
                        assignee {
                            _id
                            email
                            firstname
                            lastname
                        }
                        defaultActionType
                        exitguards {
                            nextStageId
                            conditions {
                                assertation {
                                    ... on ActionAssertation {
                                        customstatus
                                        type
                                        status
                                    }
                                }
                            }
                        }
                        autoprogress {
                            nextStageId
                            conditions {
                                assertation {
                                    ... on ActionAssertation {
                                        customstatus
                                        type
                                        status
                                    }
                                }
                            }
                        }
                    }
                    StartDate
                    description
                    name
                    _id
                }
            }
        }
    }
`;

const loadWorkflowInstancesUsingCurrentModelQuery = gql`
    query workflowinstances {
        workflowinstances(filter: { project: "CURRENT" }) {
            edges {
                node {
                    _id
                    workflowmodelid
                }
            }
        }
    }
`;

const updateWorkflowModelMutation = gql`
    mutation updateWorkflowModel($input: WorkflowmodelInput!, $id: ID!) {
        updateWorkflowmodel(input: $input, id: $id) {
            stages {
                _id
                name
                description
                defaultActionType
                nextStageTransitions {
                    to
                    name
                    description
                }
                prevStageTransitions {
                    to
                    name
                    description
                }
                PlannedDuration
                onEnter {
                    explicit_deny {
                        subject
                        kind
                        permission
                        scope
                    }
                    createActions {
                        kind
                        name
                        customstati
                        subject {
                            id
                            kind
                        }
                    }
                }
                assignee {
                    _id
                    email
                    firstname
                    lastname
                }
                defaultActionType
                exitguards {
                    nextStageId
                    conditions {
                        assertation {
                            ... on ActionAssertation {
                                customstatus
                                type
                                status
                            }
                        }
                    }
                }
                autoprogress {
                    nextStageId
                    conditions {
                        assertation {
                            ... on ActionAssertation {
                                customstatus
                                type
                                status
                            }
                        }
                    }
                }
            }
            StartDate
            description
            name
            _id
        }
    }
`;

const applyWorkflowModelMutation = gql`
    mutation applyWorkflowModelChange($id: ID!, $workflowInstances: [ID!]!, $workflowstagemap: JSONObject) {
        applyWorkflowModelChange(workflowModel: $id, workflowInstances: $workflowInstances, workflowstagemap: $workflowstagemap) {
            _id
            stages {
                _id
                name
            }
        }
    }
`;

const enableWorkflowInstancesMutation = gql`
    mutation enabledisableWorkflowInstance($workflowInstances: [ID!]!) {
        enabledisableWorkflowInstance(workflowInstances: $workflowInstances, enabled: true) {
            _id
            disabled
            stages {
                _id
                name
            }
        }
    }
`;

export const loadWorkflowModel = async ({ client }) => {
    const { data: { currentProject: { activeHeadingWorkflowModel: data } = {} } = {} } = await client.query({
        query: loadActiveWorkflowModelQuery,
        fetchPolicy: "no-cache",
    });
    return data;
};

export const loadWorkflowInstances = async ({ modelId }, { client }) => {
    const { data: { workflowinstances: data } = {} } = await client.query({
        query: loadWorkflowInstancesUsingCurrentModelQuery,
        fetchPolicy: "no-cache",
    });
    return data;
};

export const activateWorkflowModel = async ({ model: { _id, ...model } }, { client }) => {
    const { data: { updateWorkflowmodel } = {} } = await client.mutate({
        mutation: updateWorkflowModelMutation,
        // eslint-disable-next-line no-underscore-dangle
        variables: { input: model, id: _id },
    });
    return updateWorkflowmodel;
};

export const updateWorkflowModel = async ({ model: { _id, ...model }, workflowInstances, deletedStageMap, workflowFor }, { client }) => {
    const { data: { updateWorkflowmodel } = {} } = await client.mutate({
        mutation: updateWorkflowModelMutation,
        // eslint-disable-next-line no-underscore-dangle
        variables: { input: { ...model, workflowFor }, id: _id },
    });

    const stagemapWithGeneratedIds = Object.fromEntries(
        Object.entries(deletedStageMap).map(([deletedStage, newStage]) => {
            const newStageIndex = model?.stages?.findIndex(({ _id: stageId }) => stageId === newStage);
            const newStageObj = newStageIndex && updateWorkflowmodel?.stages?.find((_, idx) => idx === newStageIndex);
            if (newStageObj?._id) {
                return [deletedStage, newStageObj._id];
            }
            return [deletedStage, newStage];
        })
    );
    if (workflowFor === "DOCUMENT") {
        await client.mutate({
            mutation: enableWorkflowInstancesMutation,
            // eslint-disable-next-line no-underscore-dangle
            variables: { workflowInstances: workflowInstances || [] },
        });
    }

    await client.mutate({
        mutation: applyWorkflowModelMutation,
        // eslint-disable-next-line no-underscore-dangle
        variables: { id: _id, workflowInstances, workflowstagemap: stagemapWithGeneratedIds },
    });
    return updateWorkflowmodel;
};

export const getFormattedHeadingIDsByProject = async ({ mdContext }) => {
    var result = [];
    if (window?.APPVERSION?.directory) {
        try {
            const doc = await new Promise((reso, rej) => {
                window.app.Duchess.listObject(
                    "Document",
                    {
                        filterBy: "usedInProject",
                        filter: window.app.Duchess.getCookie("CurrentProjectID"),
                    },
                    res => {
                        return res.success ? reso(res) : rej(res);
                    }
                );
            });

            const { list: v2Doclist, success, message } = doc;

            if (success) {
                var filteredDocuments = v2Doclist?.filter(d => d?.archived !== 1) || [];
                for (var i = 0; i <= filteredDocuments.length - 1; i++) {
                    const doc = filteredDocuments[i];
                    const v2Doc = await new Promise((reso, rej) => {
                        window.app.Duchess.readObject(
                            "Document",
                            {
                                ID: doc?.id,
                                populate: "conformsToStructure,conformsToStructure.headings",
                            },
                            res => {
                                return res.success ? reso(res) : rej(res);
                            }
                        );
                    });

                    var docHeadingIds = v2Doc?.object?.conformsToStructure?.headings.map(o => `${mdContext?.project}:${doc?.id}-${o.id}`);
                    result.push(...docHeadingIds);
                }
                return result;
            }

            throw new Error(message);
        } catch (e) {
            console.error(e);
            return [];
        }
    }

    // Not in v2 - just return v3 data
    return [];
};

export const loadAllUsers = async ({ client }) => {
    const {
        data: {
            currentProject: {
                availableusers: { edges },
                groups,
            },
        },
        errors,
    } = await client.query({
        query: loadAllUsersQuery,
        fetchPolicy: "no-cache",
    });
    return { result: { users: edges?.map(({ node }) => node) || [], groups }, errors };
};
